import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Lock, Zap, Shield, Users, Vote, Trophy, ArrowRight, Clock, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: Users, label: 'Team Hub Access', desc: 'Exclusive team community' },
  { icon: Vote, label: 'Vote in Polls', desc: 'Shape your team\'s future' },
  { icon: Trophy, label: 'Draft Participation', desc: 'Cast votes & join live drafts' },
  { icon: Shield, label: 'Pro Dashboard', desc: 'Personalized member experience' },
];

export const ProGate = ({ feature = 'this feature' }) => {
  const { trialUsed, fetchUser, isAdminRole, user } = useAuth();
  const [starting, setStarting] = useState(false);

  // Phase 8.4 — Staff Preview Access: ADMIN_ROLES (front_office_staff /
  // team_admin) bypass Pro paywalls everywhere via permission-based
  // access. ProRoute in App.js skips this gate when isPro=true (which
  // is true for admins), so this branch is defense-in-depth: even if a
  // page renders <ProGate /> directly, admins will see a "Staff
  // Preview" notice instead of a paywall and will never be redirected
  // to Stripe checkout.
  if (isAdminRole) {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center py-12 px-4"
        data-testid="pro-gate-staff-preview"
      >
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-5 bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
            <Shield className="w-7 h-7 text-amber-300" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300 mb-2">
            Staff Preview Access Active
          </div>
          <h1 className="font-heading font-bold text-2xl uppercase tracking-tight mb-3">
            You have full preview access
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            As {user?.role === 'team_admin' ? 'a Team Admin' : 'Front Office Staff'},
            you can view {feature} for testing and support — no Pro subscription needed.
          </p>
          <Link to="/league-admin" data-testid="staff-preview-back-to-admin">
            <Button
              variant="outline"
              className="font-heading uppercase tracking-wider border-amber-500/40 hover:bg-amber-500/10"
            >
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              Back to League Admin
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleStartTrial = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/member/start-trial`);
      toast.success(res.data.message);
      await fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start trial');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center py-12 px-4" data-testid="pro-gate">
      <div className="max-w-lg w-full text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
          <Lock className="w-8 h-8 text-amber-400" />
        </div>

        <h1 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight mb-3">
          Pro Members Only
        </h1>
        <p className="text-muted-foreground text-sm mb-8 max-w-md mx-auto">
          Upgrade to Pro to unlock {feature} and get the full Federation Sports experience.
        </p>

        <div className="grid grid-cols-2 gap-3 mb-8">
          {PERKS.map(perk => (
            <div
              key={perk.label}
              className="bg-card border border-border p-3 text-left"
            >
              <perk.icon className="w-4 h-4 text-amber-400 mb-1.5" />
              <p className="font-heading font-bold uppercase text-[11px] tracking-wider">{perk.label}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{perk.desc}</p>
            </div>
          ))}
        </div>

        {/* Free Trial CTA */}
        {!trialUsed && (
          <div className="mb-4">
            <Button
              onClick={handleStartTrial}
              disabled={starting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-heading uppercase tracking-wider h-12"
              data-testid="start-trial-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {starting ? 'Activating...' : 'Start 7-Day Free Trial'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-[10px] text-muted-foreground mt-2 font-mono flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" />
              No credit card required. Full Pro access for 7 days.
            </p>
          </div>
        )}

        {trialUsed && (
          <div className="bg-muted/50 border border-border p-3 mb-4 text-center">
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
              Your free trial has ended
            </p>
          </div>
        )}

        <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-amber-500/10 border border-amber-500/20 p-4 mb-6">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Crown className="w-5 h-5 text-amber-400" />
            <span className="font-heading font-bold text-lg text-amber-400">$14.99</span>
            <span className="text-xs text-muted-foreground">/month</span>
          </div>
          <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
            Full access to all Pro features
          </p>
        </div>

        <Link to="/membership" data-testid="pro-gate-upgrade-btn">
          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black font-heading uppercase tracking-wider">
            <Zap className="w-4 h-4 mr-2" />
            Upgrade to Pro
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>

        <p className="text-[10px] text-muted-foreground mt-4 font-mono">
          Cancel anytime. Access continues until billing period ends.
        </p>
      </div>
    </div>
  );
};
